import { Observable, Subject } from 'rxjs/Rx'

import { ModelRepositoryAdapter } from '../../../model-repository/model-repository-adapter'
import { ModelRepository } from '../../../model-repository/model-repository'

import { PathNode, Task, UserTask } from './../../models'
import { AppContext } from '../../../helpers'

export class UserTaskService extends ModelRepositoryAdapter<UserTask> {

  private _taskEmitters: WeakMap<Task, {[action: string]: Subject<UserTask>}>
  private _cachedUserTasks: UserTask[] = []
  private _cacheInitialized: boolean = false

  constructor(
    _modelRepository: ModelRepository,
    private _appContext: AppContext
  ) {
    super(_modelRepository, UserTask)
    this.initialize(['task'])
    this._taskEmitters = new WeakMap<Task, {[action: string]: Subject<UserTask>}>()

    this.installHooks()
  }

  /**
   * Creates a new user task for the given task. There's no check performed, whether the task already has a UserTask or not.
   *
   * @return A new transient/non-persistent UserTask instance for the given task for the current user
   */
  public buildUserTask(task): UserTask {
    return this.createModel({
      task: task,
      userId: this._appContext.currentUser.id
    })
  }

  /**
   * @Overridden
   */
  public findModelById(id: string, includeRelations: string[] = []): Observable<UserTask> {
    const cachedResult = () => this._cachedUserTasks.find((ut) => ut.id === id)
    if (this._cacheInitialized) {
      return Observable.of(cachedResult())
    }
    return this.findAllModels(includeRelations).map((_ignore) => cachedResult())
  }

  /**
   * @Overridden
   */
  public findAllModels(includeRelations: string[] = ['task']): Observable<UserTask[]> {
    if (this._cacheInitialized) {
      return Observable.of(this._cachedUserTasks)
    }
    return super.findAllModels.call(this, includeRelations)
      .do((allModels) => {
        this._cachedUserTasks = allModels
        this._cacheInitialized = true
      })
  }

  /**
   * @Overridden
   */
  public findModelsBy(
    searchCriteria: {[key: string]: any}, includeRelations ?: string[]
  ): Observable < UserTask[] | UserTask > {
    if (searchCriteria.task) {
      return this.findByTask(searchCriteria.task)
    }
    return super.findModelsBy(searchCriteria, includeRelations)
  }

  public save(model: UserTask): Observable<UserTask> {
    if (!model.isPersisted()) {
      this._cachedUserTasks.push(model)
    }
    return super.save(model)
  }

  public findByPathNode(pathNode: PathNode): Observable < UserTask[] > {
    return this.findByTasks(pathNode.getAllTasks())
  }

  public findByTasks(tasks: Task[]): Observable <UserTask[]> {
    if (!Array.isArray(tasks) || tasks.length === 0) {
      return Observable.of([])
    }

    const allUserTaskResults = this.findAllModels(['task']) as Observable<UserTask[]>
    return allUserTaskResults.map((userTasks: UserTask[]) =>  {
      return userTasks.filter((userTask) => tasks.includes(userTask.task))
    })
  }

  public findByTask(task: Task): Observable < UserTask[] > {
    if (typeof task === 'undefined') {
      return Observable.of(undefined)
    }

    return this.findByTasks([task])
  }

  public resetUserTasks(path: PathNode) {
    const tasks = path.getAllTasks()
    return this.findAllModels().map((userTasksResult: UserTask[]) => {
      const userTasks = [...userTasksResult]
      userTasks.forEach((userTask, _i) => {
        if (tasks.includes(userTask.task)) {
          this.delete(userTask).subscribe()
          // reset the user task in the client
          Object.assign(userTask, this.buildUserTask(userTask.task))
        }
      })
    }).map((_result) => true)
  }

  public delete(userTask: UserTask, deleteChildren: boolean = true): Observable <boolean> {
    // The order matters here, as getChildrenDeleteRequests will use the cache
    let deleteRequest = Observable.of(true)
    if (deleteChildren) {
      const childDeleteRequests = this.getChildrenDeleteRequests(userTask)
      deleteRequest = Observable.forkJoin(deleteRequest, ...childDeleteRequests)
        .map((forkedDeletedUserTasks: boolean[]) => forkedDeletedUserTasks[0])
    }
    this._deleteFromCachedUserTasks(userTask, deleteChildren)
    deleteRequest = Observable.forkJoin(super.delete(userTask), deleteRequest).map((r) => r[0])
    return deleteRequest
  }

  public onBeforeDeleteByTask(task: Task) { return this.getOrCreateTaskEmitter('onBeforeDeleteByTask', task) }
  public onAfterDeleteByTask(task: Task) { return this.getOrCreateTaskEmitter('onAfterDeleteByTask', task) }

  public onBeforeSaveByTask(task: Task) { return this.getOrCreateTaskEmitter('onBeforeSaveByTask', task) }
  public onBeforeCreateByTask(task: Task) { return this.getOrCreateTaskEmitter('onBeforeCreateByTask', task) }
  public onBeforeUpdateByTask(task: Task) { return this.getOrCreateTaskEmitter('onBeforeUpdateByTask', task) }

  public onAfterSaveByTask(task: Task) { return this.getOrCreateTaskEmitter('onAfterSaveByTask', task) }
  public onAfterCreateByTask(task: Task) { return this.getOrCreateTaskEmitter('onAfterCreateByTask', task) }
  public onAfterUpdateByTask(task: Task) { return this.getOrCreateTaskEmitter('onAfterUpdateByTask', task) }

  private _deleteFromCachedUserTasks(userTask: UserTask, deleteChildren: boolean) {
    const userTasksToDelete = [userTask]
    if (deleteChildren) {
      userTask.task.getAllChildTasks().forEach((t) => {
        const u = this._cachedUserTasks.find((ut) => ut.task === t)
        if (u) { userTasksToDelete.push(u) }
      })
    }
    userTasksToDelete.forEach((u) => this._cachedUserTasks.splice(this._cachedUserTasks.indexOf(u), 1))
  }

  private getChildrenDeleteRequests(userTask: UserTask) {
    const getChildUserTaskDeleteRequest = (childUserTasks: UserTask[]) => {
      const emptyDeleteRequest = Observable.of(null)
      const allChildrenDeleteRequests = Observable
        .forkJoin(childUserTasks.map((childUserTask: UserTask) => super.delete(childUserTask)))
        .map((forkedDeletedUserTasks: boolean[]) => forkedDeletedUserTasks[0])

      return childUserTasks.length > 0 ? allChildrenDeleteRequests : emptyDeleteRequest
    }

    return userTask.task.getAllChildTasks().map((subtask: Task) => {
      const childUserTasks = this._cachedUserTasks.filter((ut) => ut.task === subtask)
      return getChildUserTaskDeleteRequest(childUserTasks)
    })
  }

  private installHooks() {
    this.addMethodHook('before', 'save', (userTask: UserTask) => {
      if (!userTask.isPersisted()) {
        this.emit('onBeforeCreateByTask', userTask.task, userTask)
      } else {
        this.emit('onBeforeUpdateByTask', userTask.task, userTask)
      }

      this.emit('onBeforeSaveByTask', userTask.task, userTask)
    })
    this.addMethodHook('after', 'save', (userTask: UserTask, _oldUserTask: UserTask, _action: string) => {
      this.emit('onAfterSaveByTask', userTask.task, userTask)
    })
    this.addMethodHook(
      'before',
      'delete',
      (userTask: UserTask) => this.emit('onBeforeDeleteByTask', userTask.task, userTask)
    )
    this.addMethodHook('after', 'delete', (userTask: UserTask) => this.emit('onAfterDeleteByTask', userTask.task, userTask))
  }

  private getOrCreateTaskEmitter(eventListenerFnName: string, task: Task) {
    const emitters = (this._taskEmitters.get(task) || {})
    if (typeof emitters[eventListenerFnName] === 'undefined') {
      emitters[eventListenerFnName] = new Subject<UserTask>()
      this._taskEmitters.set(task, emitters)
    }
    return emitters[eventListenerFnName]
  }

  private emit(eventListenerFnName: string, task, userTask) {
    const emitter = (this._taskEmitters.get(task) || {})[eventListenerFnName]
    if (typeof emitter !== 'undefined') {
      emitter.next(userTask)
    }
  }

}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/data/services/user-task/user-task.service.ts